/**
 * Phase 54.23.8 — Hub tab strip for merged sidebar sections.
 *
 * Visual language: identical to the "Altın Envanteri" category tab
 * strip (see `gold-category-sidebar` in inventory/InventoryList.jsx).
 * That was the reference the user picked so the whole ERP feels
 * consistent when a page becomes a hub.
 *
 * Behavioural contract:
 *   - Uses <NavLink> so route changes swap the mounted page without
 *     a full app reload. React Router does the work; children pages
 *     re-mount but the top-level layout, sidebar, and auth context
 *     stay live.
 *   - Each tab is hidden when the user lacks the underlying page's
 *     permission (or does not satisfy the `superOnly` gate). Same
 *     permissions the sidebar used to enforce — zero permission drift.
 *   - Auto-redirects to the first accessible tab if the user landed
 *     on a route they cannot see (e.g. `employees.view`-only user
 *     opens `/usm/branches` — they get sent to `/usm/employees`).
 */
import React, { useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useUsmAuth } from "@/contexts/UsmAuthContext";

const SECTIONS = {
  pos: [
    { to: "/usm/pos",          label: "Ürün Satış",  perm: "pos.access" },
    { to: "/usm/price-lookup", label: "Fiyat Sorgu", perm: "price_lookup.use" },
  ],
  branches: [
    { to: "/usm/branches",  label: "Şubeler",             perm: "branches.view" },
    { to: "/usm/employees", label: "Çalışanlar",           perm: "employees.view" },
    { to: "/usm/labels",    label: "Etiket Şablonları",   perm: "settings.view" },
    { to: "/usm/notes",     label: "Notlar",               perm: null },
  ],
  system: [
    { to: "/usm/audit",         label: "Denetim",              perm: "audit.view" },
    { to: "/usm/system/backup", label: "Yedekleme & Kurtarma", perm: null, superOnly: true },
  ],
};

export default function HubTabs({ section }) {
  const { hasPerm, isSuperAdmin } = useUsmAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const all = SECTIONS[section] || [];
  const visible = useMemo(
    () => all.filter((t) => {
      if (t.superOnly) return !!isSuperAdmin;
      if (t.perm) return hasPerm(t.perm);
      return true;
    }),
    [section, hasPerm, isSuperAdmin, all]
  );

  // Redirect to first accessible sibling when the user hits a route
  // they cannot see. Runs after render so nothing paints in a "denied"
  // state. Uses `replace` so browser back doesn't ping-pong.
  useEffect(() => {
    if (!visible.length) return;
    const here = visible.find((t) => t.to === location.pathname);
    if (here) return;
    // Only auto-redirect if the current pathname is one of this
    // section's routes but the user cannot see it. Never navigate
    // away from an unrelated page.
    const belongsHere = all.some((t) => t.to === location.pathname);
    if (belongsHere) navigate(visible[0].to, { replace: true });
  }, [location.pathname, visible, all, navigate]);

  if (visible.length <= 1) return null;

  return (
    <div
      data-testid={`hub-tabs-${section}`}
      className="bg-white border border-[#E7E5E4] mb-4 px-3 py-2 flex flex-wrap items-center gap-2"
    >
      {visible.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end
          data-testid={`hub-tab-${section}-${t.to.split("/").pop()}`}
          className={({ isActive }) =>
            "text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors " +
            (isActive
              ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
              : "border-[#E7E5E4] hover:bg-[#FAFAF9]")
          }
        >
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}
